import React from "react";
import { motion } from "framer-motion";
import { chartColors, DEFAULT_ACCENT } from "@/styles/colors";

/**
 * Predefined color palette for workspace accent colors.
 * These colors are carefully selected to work well as accent colors
 * on both light and dark backgrounds.
 */
const COLOR_PALETTE = [
  // Blues
  { color: chartColors.blue, name: "Blue" },
  { color: DEFAULT_ACCENT, name: "Indigo" },
  // Purples
  { color: chartColors.violet, name: "Violet" },
  { color: "#A855F7", name: "Purple" }, // purple variant
  // Pinks
  { color: chartColors.pink, name: "Pink" },
  { color: "#F43F5E", name: "Rose" }, // rose variant
  // Reds/Oranges
  { color: chartColors.red, name: "Red" },
  { color: chartColors.orange, name: "Orange" },
  // Yellows/Greens
  { color: chartColors.yellow, name: "Yellow" },
  { color: chartColors.green, name: "Green" },
  // Teals/Cyans
  { color: chartColors.teal, name: "Teal" },
  { color: "#06B6D4", name: "Cyan" }, // cyan variant
];

interface ColorPalettePickerProps {
  /** Currently selected color (hex) */
  value: string;
  /** Callback when color is selected */
  onChange: (color: string) => void;
  /** Optional label */
  label?: string;
  /** Compact mode for smaller display */
  compact?: boolean;
  /** Optional className */
  className?: string;
}

/**
 * Simple color palette picker for workspace accent colors.
 * Replaces the complex arc-color-wheel with a straightforward grid of preset colors.
 * 
 * @example
 * ```tsx
 * <ColorPalettePicker
 *   value={accentColor}
 *   onChange={setAccentColor}
 *   label="Workspace Color"
 * />
 * ```
 */
const ColorPalettePicker: React.FC<ColorPalettePickerProps> = ({
  value,
  onChange,
  label,
  compact = false,
  className = "",
}) => {
  const colorSize = compact ? "w-6 h-6" : "w-8 h-8";
  const gap = compact ? "gap-1.5" : "gap-2";

  return (
    <div className={`color-palette-picker ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-[var(--arc-sidebar-text-secondary)] mb-2">
          {label}
        </label>
      )}

      <div className={`flex flex-wrap ${gap}`}>
        {COLOR_PALETTE.map(({ color, name }) => {
          const isSelected = value?.toLowerCase() === color.toLowerCase();
          
          return (
            <motion.button
              key={color}
              type="button"
              whileHover={{ scale: 1.15 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onChange(color)}
              className={`${colorSize} rounded-full border-2 transition-all cursor-pointer ${
                isSelected
                  ? "border-white ring-2 ring-offset-1 ring-offset-[var(--arc-sidebar-background)] ring-white/50 shadow-lg"
                  : "border-transparent hover:border-white/30"
              }`}
              style={{ backgroundColor: color }}
              title={name}
              aria-label={`Select ${name} color`}
              aria-pressed={isSelected}
            />
          );
        })}
      </div>
    </div>
  );
};

export default ColorPalettePicker;

/**
 * Get the color palette array for external use
 */
export const getColorPalette = () => COLOR_PALETTE;

/**
 * Get a color name from its hex value
 */
export const getColorName = (hex: string): string | undefined => {
  const found = COLOR_PALETTE.find(
    (c) => c.color.toLowerCase() === hex.toLowerCase()
  );
  return found?.name;
};
